import React, { useEffect, useRef, useState } from 'react'
import * as Sentry from '@sentry/browser'
import { PRE_CHECK_MACHINE_STATES } from '../constants'
import { sanitizeDataForSentry } from '../../../services/util'
import useStudentPreChecks from '../useStudentPreChecks'
import useWindowEvents from '../useWindowEvents'
import useASRStoryPreload from '../hooks/useASRStoryPreload'
import { VOLUME_CHECK_MODE } from './StudentVolumeChecksConstants'
import VolumeCheckModes from './VolumeCheckModes'
 
export default function StudentVolumeChecks(props) {

    /**
     * props
     */
    const { speechProps, toggleReaderContainer, showMicIcon } = props

    /**
    * states
    */
    const [currentMode, setCurrentMode] = useState(VOLUME_CHECK_MODE.STUDENT)

    /**
     * hooks
     */
    useWindowEvents()
    const studentPreCheck = useStudentPreChecks({ itemToCheck: PRE_CHECK_MACHINE_STATES.studentSpeechVolumeChecks });
    const { isCurrent, currentError, settings } = studentPreCheck;
    const { preloadASRStory, clearPreloadedStory } = useASRStoryPreload()

    /**
     * mutable values, remember to use refVariable.current to access or mutate the value!
     */
    const checkStarted = useRef(false)
    const teacherCalled = useRef(false)
    const teacherTestSuccessful = useRef(false)

    /**
     * effects
     */
    useEffect(() => {
        if (isCurrent && checkStarted.current === false) {
            checkStarted.current = true
            studentPreCheck.startCheck()
            
            // Start preloading ASR story in background
            preloadASRStory()
        }
    }, [isCurrent])

    // Cleanup preloaded story if volume check fails or component unmounts
    useEffect(() => {
        return () => {
            if (currentError) {
                clearPreloadedStory()
            }
        }
    }, [currentError])

    const toggleTeacherMode = async (isTeacherMode) => {
      return new Promise((resolve) => {
        speechProps.abortDance()
        speechProps.abortSpeech(() => {
          if(isTeacherMode) setCurrentMode(VOLUME_CHECK_MODE.TEACHER)
          else setCurrentMode(VOLUME_CHECK_MODE.STUDENT)
          resolve()
        })
      })
    }

    const logVolumeCheckFailure = (failureType, extraData = {}) => {
        const maxVolume = extraData?.volumeStats?.maxVolume;
        // Get recorder error logs if available
        const recorderErrorLogs = speechProps.getRecorderErrorLogs ? speechProps.getRecorderErrorLogs() : null;
        console.log('_sentryMicErrorLogs before VOLUME_CHECK_FAILED:', JSON.stringify(recorderErrorLogs));
        
        Sentry.captureMessage('VOLUME_CHECK_FAILED', {
            level: 'warning',
            tags: {
                component: 'StudentVolumeChecks',
                failureType: failureType,
                teacherMode: isTeacherMode.toString(),
                maxVolume: typeof maxVolume === 'number' ? Math.round(maxVolume / 10) * 10 : null, // round to nearest 10 for Sentry filtering
            },
            extra: sanitizeDataForSentry({
                failureType: failureType,
                teacherMode: isTeacherMode,
                ...extraData
            })
        });
    }

    /**
     * Rendering
     */
    const isTeacherMode = currentMode === VOLUME_CHECK_MODE.TEACHER
    
    if (!studentPreCheck.isCurrent) return null

    if( isTeacherMode ){
        return <VolumeCheckModes 
        key="volume-check-teacher-mode"
        speechProps={speechProps} 
        toggleReaderContainer={toggleReaderContainer}
        showMicIcon={showMicIcon}
        teacherCalled={teacherCalled}
        isTeacherMode={isTeacherMode} 
        teacherTestSuccessful={teacherTestSuccessful}
        toggleTeacherMode={toggleTeacherMode}
        logVolumeCheckFailure={logVolumeCheckFailure}/>
    }
    
    return <VolumeCheckModes 
        key="volume-check-student-mode"
        speechProps={speechProps} 
        toggleReaderContainer={toggleReaderContainer}
        showMicIcon={showMicIcon}
        teacherCalled={teacherCalled}
        teacherTestSuccessful={teacherTestSuccessful}
        isTeacherMode={isTeacherMode} 
        toggleTeacherMode={toggleTeacherMode}
        logVolumeCheckFailure={logVolumeCheckFailure}/>
}